import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { useEffect, useState } from 'react';
import { PrimaryButton, SecondaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { observer } from 'mobx-react';
import { useLocation, useParams } from 'react-router-dom';
import { Disable } from '@monorepo/base/src/components/disable/disable';
import { BottomBar, BottomBarActions } from '@monorepo/base/src/components/bottom-bar/bottom-bar';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { ErrorMsg } from '../../../modules/components/error-msg/error-msg';
import { IMFPStore } from '../../../modules/stores';
import { AdvertiserForm } from '../../../modules/components/forms/advertiser/advertiser.form';
import { useAdvertiserGet } from '../../../modules/hooks/apis/use-advertiser-get';
import { BetweenPagesStateProps } from '@monorepo/tools/src/lib/interfaces/global';
import { SiderSlider } from '@monorepo/base/src/components/sider-slider/sider-slider';
import styles from './edit-advertiser.module.scss';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { getUrlLevelUp } from '@monorepo/tools/src/lib/utils/url';
import { useNavigateParams } from '@monorepo/tools/src/lib/hooks/tools/use-navigate-params';
import { Helmet } from 'react-helmet-async';

export const EditAdvertiser = observer(() => {
	const { advertiserStore } = useStores<IMFPStore>();
	const { dispatchLog } = useLogs();
	const [isFormClicked, setFormClicked] = useState<boolean>(false);
	const advertiserCrudStore = advertiserStore.getCrud();
	const advertiser = advertiserCrudStore.getData();

	const navigate = useNavigateParams();
	const location = useLocation();
	const params = useParams();
	if (params.advertiserId) {
		useAdvertiserGet({ id: params.advertiserId });
	}

	useEffect(() => {
		advertiserCrudStore.reset();
	}, [location]);

	const name = advertiser.getName();
	const formStore = advertiserCrudStore.getFormStore();
	const isValidForm = formStore.getIsValid();
	const isLoading = advertiserCrudStore.getIsLoading();
	const isSuccess = advertiserCrudStore.getIsSuccess();
	const error = advertiserCrudStore.getHttpError();

	useEffect(() => {
		if (!isValidForm && isFormClicked) {
			dispatchLog({
				msg: 'Please complete all mandatory fields',
				type: DispatchLogsTypes.Error,
			});
		}
	}, [isFormClicked]);

	useEffect(() => {
		if (isSuccess) {
			navigate(getUrlLevelUp({ pathname: location.pathname }), { state: { isBlockRequest: true } as BetweenPagesStateProps });
			dispatchLog({
				msg: `Advertiser ${name} was successfully edited`,
				type: DispatchLogsTypes.Success,
			});
			advertiserCrudStore.setIsSuccess(false);
		}
	}, [isSuccess]);

	const onEditAdvertiser = () => {
		setFormClicked(true);
		advertiserStore.getCrud().edit();
	};

	return (
		<SiderSlider onClose={() => navigate('..', { state: { isBlockRequest: true } as BetweenPagesStateProps })} isOpen={true}>
			<Disable is={isLoading}>
				<Helmet>
					<title>Edit - {name || 'Advertiser'}</title>
				</Helmet>
				<Page unstyled className={styles.wrapper}>
					<Pagebar>
						<PagebarTitle classes={{ title: styles.title }} unstyled>
							Edit Advertiser
						</PagebarTitle>
					</Pagebar>
					<AdvertiserForm />
					<BottomBar classes={{ barClassses: styles.advertiserBottomBar, wrapperClassses: styles.advertiserBottomBarWrapper }}>
						{error ? <ErrorMsg error={error} classes={{ error: styles.errorMsg }} /> : null}
						<BottomBarActions>
							<SecondaryButton
								onClick={() =>
									navigate(getUrlLevelUp({ pathname: location.pathname }), {
										state: { isBlockRequest: true } as BetweenPagesStateProps,
									})
								}
								debugProps={{ dataAttrs: [new DataAttribute('id', 'cancel_advertiser_button')] }}>
								Cancel
							</SecondaryButton>
							<PrimaryButton
								onClick={() => onEditAdvertiser()}
								debugProps={{ dataAttrs: [new DataAttribute('id', 'save_advertiser_button')] }}>
								Save
							</PrimaryButton>
						</BottomBarActions>
					</BottomBar>
				</Page>
			</Disable>
		</SiderSlider>
	);
});
